import { IconName } from '@travelata-libs/ui-kit';
import { type HotelFeature } from '../types/HotelFeature';
import { BeachTypeId } from '../types/IBeachType';
import { type IDistances } from '../types/IDistances';
import getBeachType from './getBeachType';

interface GetHotelFeaturesParams {
  attributes: number[];
  distances: IDistances;
  types: number[];
  placement: number | null;
}

interface FeatureRule {
  show: boolean;
  gatedByIconsCount?: boolean;
  requiresPlaceLeft?: boolean;
  feature: HotelFeature;
}

interface HotelFeatureFlags {
  nearTheBeach: boolean;
  nearTheLift: boolean;
  nearTheCenter: boolean;
  withKids: boolean;
  forCouples: boolean;
  adultsOnly: boolean;
  airConditioner: boolean;
  wifi: boolean;
  wellness: boolean;
  beachType?: BeachTypeId;
}

const MAX_FEATURE_CHIPS = 5;
const NEAR_DISTANCE_M = 500;
const NEAR_CENTER_M = 1000;
const KM_THRESHOLD_M = 1000;
const KIDS_MIN_ATTRS = 2;

const TYPE_BEACH = 1;
const TYPE_CITY = 2;
const TYPE_LIFT = 3;
const TYPE_WELLNESS = 4;
const TYPE_KIDS = 5;
const TYPE_COUPLES = 8;

const AIR_CONDITIONER_IDS = [83, 212, 233];
const WIFI_IDS = [74, 88, 62];
const FOR_COUPLES_IDS = [169, 220, 190];
const KIDS_IDS = [111, 112, 175, 114, 163];
const NEAR_LIFT_ID = 213;
const ADULTS_ONLY_ID = 217;

const BEACH_TYPE_LABEL: Record<BeachTypeId, string> = {
  [BeachTypeId.SandBeach]: 'Песчаный пляж',
  [BeachTypeId.PebbleBeach]: 'Галечный пляж',
  [BeachTypeId.SandPebbleBeach]: 'Песчано-галечный пляж',
};

const formatDistance = (meters: number): string =>
  meters < KM_THRESHOLD_M ? `${meters} м` : `${Math.round((meters / KM_THRESHOLD_M) * 10) / 10} км`;

const isWithinDistance = (distance: number | null | undefined, max: number): boolean =>
  Boolean(distance && distance > 0 && distance < max);

const getDistanceChip = ({ types, distances }: GetHotelFeaturesParams): HotelFeature | null => {
  const distanceType = [TYPE_BEACH, TYPE_LIFT, TYPE_CITY].find((type) => types.includes(type));

  switch (distanceType) {
    case TYPE_BEACH: {
      const beach = distances.beach || distances.beachMax;
      return beach ? { key: 'distance-beach', icon: IconName.Beach, label: `${formatDistance(beach)} до пляжа` } : null;
    }
    case TYPE_LIFT: {
      return distances.lift
        ? { key: 'distance-lift', icon: IconName.Ski, label: `${formatDistance(distances.lift)} до подъёмника` }
        : null;
    }
    case TYPE_CITY: {
      return distances.center
        ? { key: 'distance-center', icon: IconName.Pin, label: `${formatDistance(distances.center)} до центра города` }
        : null;
    }
    default: {
      return null;
    }
  }
};

const getFeatureFlags = ({ attributes, distances, types, placement }: GetHotelFeaturesParams): HotelFeatureFlags => {
  const attributeIds = new Set(attributes);
  const hasAttribute = (id: number): boolean => attributeIds.has(id);
  const hasAnyAttribute = (ids: number[]): boolean => ids.some((id) => hasAttribute(id));

  const hasKids = types.includes(TYPE_KIDS) || KIDS_IDS.filter((id) => hasAttribute(id)).length > KIDS_MIN_ATTRS;
  const hasAdultsOnly = hasAttribute(ADULTS_ONLY_ID);
  const isBothAdultsAndKids = hasKids && hasAdultsOnly;

  const nearTheBeach =
    isWithinDistance(distances.beach, NEAR_DISTANCE_M) ||
    isWithinDistance(distances.beachMax, NEAR_DISTANCE_M) ||
    (placement !== null && placement > 0);

  return {
    nearTheBeach,
    nearTheLift: hasAttribute(NEAR_LIFT_ID),
    nearTheCenter: isWithinDistance(distances.center, NEAR_CENTER_M),
    withKids: hasKids && !isBothAdultsAndKids,
    forCouples: types.includes(TYPE_COUPLES) || hasAnyAttribute(FOR_COUPLES_IDS),
    adultsOnly: hasAdultsOnly && !isBothAdultsAndKids,
    airConditioner: hasAnyAttribute(AIR_CONDITIONER_IDS),
    wifi: hasAnyAttribute(WIFI_IDS),
    wellness: types.includes(TYPE_WELLNESS),
    beachType: getBeachType(attributes) || undefined,
  };
};

const countWeightyIcons = (flags: HotelFeatureFlags): number =>
  Number(flags.nearTheBeach || flags.nearTheLift || flags.nearTheCenter) +
  Number(flags.airConditioner) +
  Number(flags.wifi) +
  Number(flags.forCouples || flags.adultsOnly) +
  Number(flags.withKids) +
  Number(!!flags.beachType) +
  Number(flags.wellness);

const buildFeatureRules = (flags: HotelFeatureFlags): FeatureRule[] => [
  { show: flags.nearTheBeach, feature: { key: 'near-beach', icon: IconName.Beach, label: 'Близко к пляжу' } },
  {
    show: flags.nearTheLift,
    gatedByIconsCount: true,
    feature: { key: 'near-lift', icon: IconName.Ski, label: 'Близко к подъемнику' },
  },
  {
    show: flags.nearTheCenter,
    gatedByIconsCount: true,
    feature: { key: 'near-center', icon: IconName.Pin, label: 'Близко к центру' },
  },
  { show: flags.withKids, feature: { key: 'kids', icon: IconName.Child, label: 'Отдых с детьми' } },
  { show: flags.forCouples, feature: { key: 'couples', icon: IconName.Heart, label: 'Идеально для отдыха парой' } },
  {
    show: flags.airConditioner,
    feature: { key: 'air-conditioning', icon: IconName.Conditioning, label: 'Кондиционер' },
  },
  { show: flags.wifi, feature: { key: 'wifi', icon: IconName.Wifi, label: 'Интернет' } },
  {
    show: flags.adultsOnly,
    feature: { key: 'adults-only', icon: IconName.AdultsOnly, label: 'Только для взрослых' },
  },
  {
    show: !!flags.beachType,
    requiresPlaceLeft: true,
    feature: { key: 'beach-type', label: BEACH_TYPE_LABEL[flags.beachType ?? BeachTypeId.SandBeach] },
  },
  {
    show: flags.wellness,
    requiresPlaceLeft: true,
    feature: { key: 'wellness', icon: IconName.Spa, label: 'Оздоровительные туры' },
  },
];

const selectFeatures = (rules: FeatureRule[], iconsCount: number): HotelFeature[] => {
  const selected: HotelFeature[] = [];
  let placesLeft = MAX_FEATURE_CHIPS;

  rules.forEach((rule) => {
    if (!rule.show) return;
    if (rule.gatedByIconsCount && placesLeft - iconsCount < 0) return;
    if (rule.requiresPlaceLeft && placesLeft <= 0) return;
    selected.push(rule.feature);
    placesLeft -= 1;
  });

  return selected;
};

export default function getHotelFeatures(params: GetHotelFeaturesParams): HotelFeature[] {
  const flags = getFeatureFlags(params);
  const features = selectFeatures(buildFeatureRules(flags), countWeightyIcons(flags));

  const distanceChip = getDistanceChip(params);

  return distanceChip ? [distanceChip, ...features] : features;
}
